import { Link } from "react-router-dom";
import type { DateRange } from "../../types";
import { Button } from "@tellescope/ui";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";

export interface PaginationControlsProps extends DateRange {
  onPreviousPageClick: (() => void) | string;
  onNextPageClick: (() => void) | string;
  replace?: boolean;
  timezone?: string;
}

export const PaginationControls = ({
  startDate,
  endDate,
  onPreviousPageClick,
  onNextPageClick,
  replace = true,
  timezone,
}: PaginationControlsProps) => {
  const { formatInTimeZone } = useDateFnsFormattersWithLocale();
  const previousIsLink = typeof onPreviousPageClick === "string";
  const nextIsLink = typeof onNextPageClick === "string";

  const dateDisplayText = useMemo(() => {
    const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isSameDay = formatInTimeZone(startDate, tz, "yyyy-MM-dd") === formatInTimeZone(endDate, tz, "yyyy-MM-dd");
    const result = [formatInTimeZone(startDate, tz, "MMM d, yyyy")];
    if (!isSameDay) result.push(formatInTimeZone(endDate, tz, "MMM d, yyyy"));
    return result.join(" - ");
  }, [endDate, formatInTimeZone, startDate, timezone]);

  return (
    <div className="flex items-center gap-2">
      <Button
        asChild={previousIsLink}
        onClick={previousIsLink ? undefined : onPreviousPageClick}
        variant="ghost"
        size="sm-icon"
      >
        {previousIsLink ? (
          <Link to={onPreviousPageClick} replace={replace}>
            <ChevronLeft />
          </Link>
        ) : (
          <ChevronLeft />
        )}
      </Button>
      <p className="text-sm text-muted-foreground font-light">{dateDisplayText}</p>
      <Button asChild={nextIsLink} onClick={nextIsLink ? undefined : onNextPageClick} variant="ghost" size="sm-icon">
        {nextIsLink ? (
          <Link to={onNextPageClick} replace={replace}>
            <ChevronRight />
          </Link>
        ) : (
          <ChevronRight />
        )}
      </Button>
    </div>
  );
};
